import {
    HttpErrorResponse,
    HttpEvent,
    HttpHandler,
    HttpInterceptor,
    HttpRequest,
    HttpStatusCode,
} from '@angular/common/http';
import { Injectable, InjectionToken } from '@angular/core';
import { Router } from '@angular/router';
import Debug from 'debug';
import { Observable, forkJoin } from 'rxjs';
import { catchError, map, mergeMap, timeout } from 'rxjs/operators';
import { blobToJsonError } from '../../../../aumico-lib-core-fe/src/lib';
import { INTERCEPTOR_IGNORED_METHODS } from '../constants/interceptor-ignored-methods';
import { AuthService } from '../services/auth.service';
import { PublicApiUtil } from '../util/PublicApisUtils';
const debug = Debug('aumico:aumico-lib-users-fe:AuthHandler');

export interface IAuthHandlerDelegate {
    onSessionExpired(): void;
}

export const AUTH_INTERCEPTOR = new InjectionToken<IAuthHandlerDelegate>('AUTH_INTERCEPTOR');

/**
 * REV-21869 #162 (N-2.5, part 2) — bound the write-side token resolution so a stalled CSRF round
 * trip cannot hang a mutating request forever (account-orchestrator.spec.ts case 29). `forkJoin`
 * below waits for BOTH `getIdToken()` and `getCsrfToken()` to COMPLETE — a dropped connection makes
 * `getCsrfToken()` error, which `forkJoin` already propagates today (case 28 is green on exactly
 * that path), but a connection that is merely STALLED never errors and never completes, so
 * `forkJoin` never emits at all: no request is ever created, and nothing downstream — not the
 * effect's `catchError`, not the UI — ever gets a chance to say anything.
 *
 * 10s, matching the existing "never hang on a slow/failed dependency" guard in this same feature
 * area (`abcp-preview-redirect.guard.ts:31`). Case 29's own trace measured the whole gesture
 * end-to-end at ~130ms when nothing is broken, so 10s is far outside anything a genuinely slow but
 * alive connection would need, while still leaving most of the suite's 25s observation window free
 * for the resulting failure to propagate through the effect, the reducer and the UI. Deliberately
 * NOT applied to the ignored-methods (GET/HEAD/OPTIONS) branch below — that path does not carry
 * this failure mode today and touching it is out of this ticket's scope.
 */
const CSRF_TOKEN_RESOLUTION_TIMEOUT_MS = 10_000;

@Injectable()
export class AuthHandler implements HttpInterceptor {
    constructor(
        private readonly authService: AuthService,
        private readonly publicApisUtils: PublicApiUtil,
        private readonly router: Router, // private readonly localStoargaService: LocalStorageService
    ) { }

    intercept(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
        const PUBLIC_APIS = this.publicApisUtils.publicApis;
        /** APIs that don't need token or csrf **/
        const isMatchedUrl = PUBLIC_APIS.some((value) => {
            /**
             This is used for urls with dynamic parameters to validate if current request is equal to any of
             public apis urls regardless this dynamic value
            **/
            if (value.url.includes('$')) {
                const paramName = /\$(.*?)\//g.exec(value.url);
                const startIndex = value.url.indexOf('$');

                const lastIndex =
                    request.url.substring(startIndex).indexOf('/') > -1
                        ? request.url.substring(startIndex).indexOf('/') + startIndex
                        : request.url.length;
                const paramValue = request.url.substring(startIndex, lastIndex);

                const modifiedUrl = value.url.replace(paramName[0], paramValue);

                return modifiedUrl === request.url && value.methods.includes(request.method.toUpperCase());
            } else {
                return value.url === request.url && value.methods.includes(request.method.toUpperCase());
            }
        });

        const isMatchedWithParamInsideUrl = PUBLIC_APIS.some((value) => {
            if (value.regex) {
                const urlMatch = value.regex.test(request.url);
                const methodMatch = value.methods.includes(request.method.toUpperCase());
                return urlMatch && methodMatch;
            } else {
                return value.url === request.url && value.methods.includes(request.method.toUpperCase());
            }
        });

        if (
            isMatchedUrl ||
            isMatchedWithParamInsideUrl ||
            request.headers.get('Content-Type')?.startsWith('image/') ||
            request.url.includes('/assets')
        ) {
            return next.handle(request);
        }
        const token$ = this.authService.getIdToken();
        const language = localStorage.getItem('currentLanguage');
        const isIgnoredMethod = INTERCEPTOR_IGNORED_METHODS.includes(request.method);
        const tokens$ = isIgnoredMethod
            ? token$.pipe(map((token) => ({ token, csrfToken: null as string | null })))
            : forkJoin({ token: token$, csrfToken: this.authService.getCsrfToken() }).pipe(
                  timeout(CSRF_TOKEN_RESOLUTION_TIMEOUT_MS),
              );
        return tokens$.pipe(
            mergeMap((tokens) => {
                if (tokens.token == null) {
                    throw new Error('authorization token is null');
                }

                if (isIgnoredMethod) {
                    request = request.clone({
                        setHeaders: { Authorization: `Bearer ${tokens.token}` },
                        withCredentials: true,
                    });
                } else {
                    if (tokens.csrfToken == null) {
                        throw new Error('csrf token is null');
                    }
                    request = request.clone({
                        setHeaders: { Authorization: `Bearer ${tokens.token}`, 'x-csrf-token': tokens.csrfToken },
                        withCredentials: true,
                    });
                }

                return next.handle(request).pipe(
                    catchError((errorResponse: HttpErrorResponse) => {
                        if (
                            errorResponse.error instanceof Blob &&
                            errorResponse.headers.get('content-type')?.includes('application/json')
                        ) {
                            // If the error response is a blob containing JSON data, parse it as JSON
                            return blobToJsonError(errorResponse);
                        } else {
                            // For other error responses, just pass them through
                            throw errorResponse;
                        }
                    }),
                    catchError((errorResponse: HttpErrorResponse) => {
                        const is2faEndpoint = request.url.includes('auth/2fa/');
                        if (
                            //unauthorized
                            errorResponse.status === HttpStatusCode.Unauthorized ||
                            // user attempting to access something he is not allowed to
                            (errorResponse.status === HttpStatusCode.Forbidden &&
                                errorResponse.error.message !== 'invalid csrf token' &&
                                !is2faEndpoint)
                        ) {
                            this.router.navigate([`${language}/logout`]);
                        } else if (
                            errorResponse.status === 403 &&
                            errorResponse.error.message === 'invalid csrf token'
                        ) {
                            // REV-22582: the two failures are handled apart. Before, one `catchError` behind
                            // the `mergeMap` also caught the RETRIED request's own error and tested the
                            // original 403 instead of it, so any failure after a refresh — a 400 from a form
                            // — signed the user out.
                            this.authService.resetCsrfToken();
                            return this.authService.getCsrfToken().pipe(
                                // No new token: the session cannot write any more.
                                catchError((csrfRefreshError: HttpErrorResponse) => {
                                    this.router.navigate([`${language}/logout`]);
                                    throw csrfRefreshError;
                                }),
                                mergeMap((newCsrfToken) => {
                                    request = request.clone({
                                        setHeaders: {
                                            'x-csrf-token': newCsrfToken,
                                        },
                                    });
                                    return next.handle(request).pipe(
                                        // The retry follows the first attempt's sign-out rule (401, or a 403
                                        // outside 2FA — a CSRF token rejected again included) without another
                                        // refresh; every other error goes to the caller.
                                        catchError((retryError: HttpErrorResponse) => {
                                            if (
                                                retryError.status === HttpStatusCode.Unauthorized ||
                                                (retryError.status === HttpStatusCode.Forbidden && !is2faEndpoint)
                                            ) {
                                                this.router.navigate([`${language}/logout`]);
                                            }
                                            throw retryError;
                                        }),
                                    );
                                }),
                            );
                        } else if (errorResponse.status === HttpStatusCode.Conflict) {
                            throw errorResponse;
                        } else if (
                            errorResponse.status === HttpStatusCode.Forbidden &&
                            is2faEndpoint
                        ) {
                            throw errorResponse;
                        }
                        return next.handle(request);
                    }),
                );
            }),
        );
    }
}
